import { useRef, useState, type ReactNode } from 'react';
import { Hash, Radio, Plus, Loader2, WifiOff, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  FieldHint,
  FieldLabel,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import { ChipMultiSelect } from '@/client/pages/Admin/shared/ChipMultiSelect';
import { MediaPageShell } from './components/MediaPageShell';
import {
  useListeningRulesQuery,
  useCreateListeningRuleMutation,
  useUpdateListeningRuleMutation,
  useDeleteListeningRuleMutation,
} from '@/client/api/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';
import type { ApiListeningRule } from '@/client/types/marketing/api';
import { AutoWatchPanel } from './AutoWatchPanel';
import { parseWatchKeyFromRuleName } from './compile-watchers';
import {
  LISTENING_QUERY_EXAMPLES,
  LISTENING_QUERY_INSERTS,
  formatChipQuery,
  tryParseFlatQuery,
  validateListeningQuery,
  type MatchMode,
  type QueryEditorMode,
} from './listening-query';

/**
 * Listening rules — the first Sources surface on real data.
 *
 * This page was previously a read-only table over fixtures whose "New rule"
 * button raised a `(mock)` toast: the PRD listed rule CRUD as a genuine hole,
 * and there was no store slice behind it at all. It now reads and writes
 * `/api/admin/v2/marketing/sources/listening-rules`.
 *
 * One honesty point kept from the original: creating a rule used not to cause
 * anything to be matched. Outlet RSS still runs on its own beat. Social
 * discovery uses the Auto-pull panel: listen handles + keywords + @mentions
 * compile into queries, Sync writes them as rules, Run discovery hits
 * POST /radar/coverage/discover (per-query skips in errors[]; 429 = cooldown).
 */

const CHANNEL_CHIP_OPTIONS = [
  { value: 'social', label: 'Social' },
  { value: 'traditional', label: 'Traditional' },
] as const;

interface DraftRule {
  name: string;
  editorMode: QueryEditorMode;
  includeTerms: string[];
  excludeTerms: string[];
  matchMode: MatchMode;
  /** Advanced include query (full boolean dialect). */
  queryInclude: string;
  /** Advanced exclude query — optional. */
  queryExclude: string;
  channels: string[];
  alert_on_match: boolean;
  alert_volume_spike: boolean;
  alert_sentiment_drop: boolean;
}

type DraftErrors = {
  name?: string;
  includeTerms?: string;
  queryInclude?: string;
  queryExclude?: string;
  channels?: string;
};

const EMPTY_DRAFT: DraftRule = {
  name: '',
  editorMode: 'simple',
  includeTerms: [],
  excludeTerms: [],
  matchMode: 'any',
  queryInclude: '',
  queryExclude: '',
  channels: ['social', 'traditional'],
  alert_on_match: true,
  alert_volume_spike: false,
  alert_sentiment_drop: false,
};

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const textareaClass =
  'min-h-[88px] w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm leading-relaxed outline-none ring-blue-500 focus:ring-2';

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">{label}</div>
      {children}
    </div>
  );
}

/** Chip composer for keywords / hashtags — Enter or comma commits a term. */
function TermChipsField({
  terms,
  onChange,
  placeholder,
  accent = 'include',
  error,
  'aria-label': ariaLabel,
}: {
  terms: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  accent?: 'include' | 'exclude';
  error?: string;
  'aria-label'?: string;
}) {
  const [draft, setDraft] = useState('');

  const addTerm = (raw: string) => {
    const pieces = raw
      .split(/[,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (pieces.length === 0) return;
    onChange(
      pieces.reduce((next, p) => {
        if (next.some((t) => t.toLowerCase() === p.toLowerCase())) return next;
        return [...next, p];
      }, terms),
    );
    setDraft('');
  };

  const chipClass =
    accent === 'include'
      ? 'bg-blue-50 text-blue-800 hover:bg-blue-100'
      : 'bg-amber-50 text-amber-900 hover:bg-amber-100';

  return (
    <div className="space-y-1.5">
      <div
        className={fieldClass(
          'flex min-h-11 flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5 transition-shadow focus-within:ring-2 focus-within:ring-blue-500',
          error,
        )}
        role="group"
        aria-label={ariaLabel}
      >
        {terms.map((t) => (
          <span
            key={t}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
              chipClass,
            )}
          >
            {t.startsWith('#') ? (
              <Hash className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            ) : null}
            <span className="truncate">{t.startsWith('#') ? t.slice(1) : t}</span>
            <button
              type="button"
              onClick={() => onChange(terms.filter((x) => x !== t))}
              className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
              aria-label={`Remove ${t}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addTerm(draft);
            }
            if (e.key === 'Backspace' && !draft && terms.length) {
              onChange(terms.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (draft.trim()) addTerm(draft);
          }}
          className="min-w-[10rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-gray-400"
          placeholder={terms.length ? 'Add another…' : placeholder}
          aria-invalid={!!error}
        />
      </div>
      <FieldHint error={error} />
    </div>
  );
}

function QueryModeToggle({
  value,
  onChange,
}: {
  value: QueryEditorMode;
  onChange: (next: QueryEditorMode) => void;
}) {
  return (
    <div
      className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5"
      role="tablist"
      aria-label="Query editor mode"
    >
      {(
        [
          ['simple', 'Simple'],
          ['advanced', 'Advanced'],
        ] as const
      ).map(([mode, label]) => {
        const on = value === mode;
        return (
          <button
            key={mode}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(mode)}
            className={cn(
              'rounded px-2.5 py-1 text-[11px] font-medium transition-colors',
              on
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-800',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function AdvancedQueryField({
  id,
  value,
  onChange,
  error,
  placeholder,
  examples,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  placeholder: string;
  examples?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const insertAtCursor = (snippet: string) => {
    const el = ref.current;
    if (!el) {
      onChange(`${value}${snippet}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    // Place caret inside paired snippets like `(  )` or `" "`.
    const innerPad = snippet === '(  )' || snippet === '" "' ? 2 : snippet.length;
    const next = value.slice(0, start) + snippet + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + innerPad;
      el.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {LISTENING_QUERY_INSERTS.map((op) => (
          <button
            key={op.label}
            type="button"
            title={op.tip}
            onClick={() => insertAtCursor(op.insert)}
            className="rounded border border-gray-200 bg-white px-2 py-0.5 font-mono text-[11px] font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
          >
            {op.label}
          </button>
        ))}
      </div>
      <textarea
        id={id}
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={fieldClass(textareaClass, error)}
        aria-invalid={!!error}
        spellCheck={false}
        placeholder={placeholder}
      />
      <FieldHint error={error} />
      {examples && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Examples
          </p>
          <ul className="space-y-1">
            {LISTENING_QUERY_EXAMPLES.map((ex) => (
              <li key={ex}>
                <button
                  type="button"
                  onClick={() => onChange(ex)}
                  className="block w-full truncate rounded px-1 py-0.5 text-left font-mono text-[11px] text-blue-700 hover:bg-blue-50"
                  title={ex}
                >
                  {ex}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const MediaListeningPage = () => {
  const { data, isLoading, isError, error } = useListeningRulesQuery({ limit: 200 });
  const createRule = useCreateListeningRuleMutation();
  const updateRule = useUpdateListeningRuleMutation();
  const deleteRule = useDeleteListeningRuleMutation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRule>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<DraftErrors>({});

  const rules: ApiListeningRule[] = data?.items ?? [];
  const enabled = rules.filter((r) => r.enabled).length;
  const matches = rules.reduce((sum, r) => sum + (r.match_count ?? 0), 0);

  const openDialog = () => {
    setDraft(EMPTY_DRAFT);
    setErrors({});
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setDraft(EMPTY_DRAFT);
    setErrors({});
  };

  const switchEditorMode = (next: QueryEditorMode) => {
    if (next === draft.editorMode) return;

    if (next === 'advanced') {
      // Promote chip state into raw queries so nothing is lost.
      setDraft((d) => ({
        ...d,
        editorMode: 'advanced',
        queryInclude: d.queryInclude.trim()
          ? d.queryInclude
          : formatChipQuery(d.includeTerms, d.matchMode),
        queryExclude: d.queryExclude.trim()
          ? d.queryExclude
          : d.excludeTerms.length
            ? formatChipQuery(d.excludeTerms, 'any')
            : '',
      }));
      setErrors((prev) => {
        const cleared = clearFieldError(prev, 'includeTerms');
        return cleared;
      });
      return;
    }

    // Advanced → Simple: only when the query is a flat AND/OR list.
    const parsedInclude = tryParseFlatQuery(draft.queryInclude);
    const parsedExclude = draft.queryExclude.trim()
      ? tryParseFlatQuery(draft.queryExclude)
      : { terms: [] as string[], matchMode: 'any' as MatchMode };

    if (!parsedInclude || !parsedExclude) {
      toast.message('This query uses nesting or operators Simple mode cannot edit', {
        description: 'Stay in Advanced, or clear the query to use chips.',
      });
      return;
    }

    setDraft((d) => ({
      ...d,
      editorMode: 'simple',
      includeTerms: parsedInclude.terms,
      matchMode: parsedInclude.matchMode,
      excludeTerms: parsedExclude.terms,
    }));
    setErrors((prev) => {
      let nextErr = clearFieldError(prev, 'queryInclude');
      nextErr = clearFieldError(nextErr, 'queryExclude');
      return nextErr;
    });
  };

  const resolveQueries = ():
    | { ok: true; query_include: string; query_exclude: string | null }
    | { ok: false } => {
    if (draft.editorMode === 'simple') {
      if (draft.includeTerms.length === 0) {
        setErrors((prev) => ({
          ...prev,
          includeTerms: 'Add at least one keyword or hashtag',
        }));
        return { ok: false };
      }
      return {
        ok: true,
        query_include: formatChipQuery(draft.includeTerms, draft.matchMode),
        query_exclude:
          draft.excludeTerms.length > 0
            ? formatChipQuery(draft.excludeTerms, 'any')
            : null,
      };
    }

    const includeIssue = validateListeningQuery(draft.queryInclude);
    const excludeIssue = draft.queryExclude.trim()
      ? validateListeningQuery(draft.queryExclude)
      : null;

    if (includeIssue || excludeIssue) {
      setErrors((prev) => ({
        ...prev,
        queryInclude: includeIssue?.message,
        queryExclude: excludeIssue?.message,
      }));
      return { ok: false };
    }

    return {
      ok: true,
      query_include: draft.queryInclude.trim(),
      query_exclude: draft.queryExclude.trim() || null,
    };
  };

  const submit = () => {
    const nextErrors: DraftErrors = {};
    if (!draft.name.trim()) nextErrors.name = 'Give this rule a name';
    if (draft.channels.length === 0) nextErrors.channels = 'Pick at least one channel';
    if (Object.keys(nextErrors).length) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return;
    }

    const resolved = resolveQueries();
    if (!resolved.ok) return;

    createRule.mutate(
      {
        name: draft.name.trim(),
        query_include: resolved.query_include,
        query_exclude: resolved.query_exclude,
        channels: draft.channels,
        // The server stores these as TINYINT, so send 0/1 rather than
        // true/false. MySQL would coerce a JSON boolean, but sending the shape
        // the column actually holds keeps the round-trip symmetrical — a rule
        // read back looks exactly like one just written.
        alert_on_match: draft.alert_on_match ? 1 : 0,
        alert_volume_spike: draft.alert_volume_spike ? 1 : 0,
        alert_sentiment_drop: draft.alert_sentiment_drop ? 1 : 0,
      },
      {
        onSuccess: () => {
          toast.success(`Rule “${draft.name.trim()}” created`);
          closeDialog();
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the rule')),
      },
    );
  };

  const previewInclude =
    draft.editorMode === 'simple'
      ? formatChipQuery(draft.includeTerms, draft.matchMode)
      : draft.queryInclude.trim();
  const previewExclude =
    draft.editorMode === 'simple'
      ? draft.excludeTerms.length
        ? formatChipQuery(draft.excludeTerms, 'any')
        : ''
      : draft.queryExclude.trim();

  return (
    <MediaPageShell
      title="Listening rules"
      description="Auto-pull public posts into Radar from listen-target handles, @mentions of owned accounts, and tracked keywords. Custom boolean rules still live below."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Sources' },
        { label: 'Listening' },
      ]}
      icon={Radio}
      actions={
        <Button type="button" className="gap-2" onClick={openDialog}>
          <Plus className="h-4 w-4" /> New rule
        </Button>
      }
      stats={[
        { label: 'Rules', value: isLoading ? '—' : String(rules.length) },
        { label: 'Enabled', value: isLoading ? '—' : String(enabled) },
        { label: 'Matches', value: isLoading ? '—' : String(matches) },
      ]}
    >
      <AutoWatchPanel />

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Rule', 'Query', 'Channels', 'Alerts', 'Matches', 'Status', ''].map((h) => (
                <th
                  key={h || 'actions'}
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center text-sm text-gray-500">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                  Loading rules…
                </td>
              </tr>
            )}

            {!isLoading && isError && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center text-sm">
                  <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
                  <div className="font-medium text-amber-800">Could not load listening rules</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {getApiErrorMessage(error, 'The marketing API did not respond')}
                  </div>
                </td>
              </tr>
            )}

            {!isLoading && !isError && rules.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center text-sm text-gray-500">
                  No listening rules yet — create one to start watching for mentions.
                </td>
              </tr>
            )}

            {rules.map((rule) => {
              const channels = Array.isArray(rule.channels) ? (rule.channels as string[]) : [];
              const alerts = [
                rule.alert_on_match && 'match',
                rule.alert_volume_spike && 'spike',
                rule.alert_sentiment_drop && 'sentiment',
              ].filter(Boolean);
              const displayName =
                parseWatchKeyFromRuleName(rule.name) != null
                  ? rule.name.replace(/\s·\swatch:[^\s]+$/, '')
                  : rule.name;
              const toggling =
                updateRule.isPending && updateRule.variables?.lrid === rule.lrid;
              const deleting =
                deleteRule.isPending && deleteRule.variables?.lrid === rule.lrid;
              return (
                <tr key={rule.lrid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{displayName}</td>
                  <td className="max-w-xs px-6 py-4">
                    <code className="block truncate rounded bg-gray-50 px-2 py-1 text-xs text-gray-700">
                      {rule.query_include}
                    </code>
                    {rule.query_exclude && (
                      <p className="mt-1 truncate text-[11px] text-gray-400">
                        exclude: {rule.query_exclude}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm capitalize text-gray-600">
                    {channels.join(', ') || '—'}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {alerts.join(' · ') || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{rule.match_count ?? 0}</td>
                  <td className="px-6 py-4">
                    <button
                      type="button"
                      disabled={toggling}
                      onClick={() =>
                        updateRule.mutate(
                          { lrid: rule.lrid, enabled: rule.enabled ? 0 : 1 },
                          {
                            onSuccess: () =>
                              toast.success(
                                rule.enabled
                                  ? `Paused “${displayName}”`
                                  : `Enabled “${displayName}”`,
                              ),
                            onError: (err) =>
                              toast.error(getApiErrorMessage(err, 'Could not update the rule')),
                          },
                        )
                      }
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        rule.enabled
                          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {toggling ? '…' : rule.enabled ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      disabled={deleting}
                      onClick={() => {
                        if (!window.confirm(`Remove listening rule “${displayName}”?`)) return;
                        deleteRule.mutate(
                          { lrid: rule.lrid },
                          {
                            onSuccess: () => toast.success(`Removed “${displayName}”`),
                            onError: (err) =>
                              toast.error(getApiErrorMessage(err, 'Could not remove the rule')),
                          },
                        );
                      }}
                      className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                    >
                      {deleting ? '…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="border-t border-gray-100 px-6 py-3 text-xs text-gray-400">
          Auto-pull queries come from the panel above. Outlet RSS still ingests traditional coverage
          on its own 15-minute beat. Social discovery is POST /radar/coverage/discover against these
          rules — not the publishing rail.
        </div>
      </div>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) closeDialog();
          else setOpen(true);
        }}
      >
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl">
          <TooltipProvider delayDuration={200}>
            <DialogHeader className="space-y-1 border-b border-gray-100 px-6 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <DialogTitle>New listening rule</DialogTitle>
                  <DialogDescription>
                    {draft.editorMode === 'simple'
                      ? 'Add keywords or hashtags. Switch to Advanced for AND / OR / NOT groups.'
                      : 'Full boolean query — AND, OR, NOT, CONTAINS, and parentheses.'}
                  </DialogDescription>
                </div>
                <QueryModeToggle value={draft.editorMode} onChange={switchEditorMode} />
              </div>
            </DialogHeader>

            <form
              className="flex flex-col"
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <div className="max-h-[min(60vh,520px)] space-y-5 overflow-y-auto px-6 py-5">
                <Field
                  label={
                    <FieldLabel
                      required
                      tip="Internal name shown in the rules list — not part of the match query."
                    >
                      Name
                    </FieldLabel>
                  }
                >
                  <input
                    value={draft.name}
                    onChange={(e) => {
                      setDraft({ ...draft, name: e.target.value });
                      setErrors((prev) => clearFieldError(prev, 'name'));
                    }}
                    className={fieldClass(inputClass, errors.name)}
                    aria-invalid={!!errors.name}
                    placeholder="Brand mentions — Ghana"
                    autoFocus
                  />
                  <FieldHint error={errors.name} />
                </Field>

                {draft.editorMode === 'simple' ? (
                  <>
                    <Field
                      label={
                        <FieldLabel
                          required
                          tip="Type a word or #hashtag, then press Enter. Combine multiple terms with Any or All below."
                        >
                          Watch for
                        </FieldLabel>
                      }
                    >
                      <TermChipsField
                        key={open ? 'include-open' : 'include-closed'}
                        terms={draft.includeTerms}
                        onChange={(includeTerms) => {
                          setDraft({ ...draft, includeTerms });
                          setErrors((prev) => clearFieldError(prev, 'includeTerms'));
                        }}
                        placeholder="Fairful or #FairfulSummer, then Enter"
                        accent="include"
                        error={errors.includeTerms}
                        aria-label="Keywords and hashtags to include"
                      />
                      <p className="text-[11px] text-gray-400">
                        Press Enter or comma to add. Prefix with # for hashtags, @ for handles.
                      </p>
                    </Field>

                    {draft.includeTerms.length > 1 && (
                      <Field
                        label={
                          <FieldLabel tip="How multiple include terms combine when matching coverage.">
                            Match when
                          </FieldLabel>
                        }
                      >
                        <div className="grid grid-cols-2 gap-2">
                          {(
                            [
                              ['any', 'Any term', 'Match if at least one appears'],
                              ['all', 'All terms', 'Match only when every term appears'],
                            ] as const
                          ).map(([mode, title, hint]) => {
                            const on = draft.matchMode === mode;
                            return (
                              <button
                                key={mode}
                                type="button"
                                onClick={() => setDraft({ ...draft, matchMode: mode })}
                                className={cn(
                                  'rounded-md border px-3 py-2 text-left transition-colors',
                                  on
                                    ? 'border-gray-900 bg-gray-900 text-white'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50',
                                )}
                              >
                                <span className="block text-xs font-semibold">{title}</span>
                                <span
                                  className={cn(
                                    'mt-0.5 block text-[11px]',
                                    on ? 'text-white/70' : 'text-gray-400',
                                  )}
                                >
                                  {hint}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </Field>
                    )}

                    <Field
                      label={
                        <FieldLabel tip="Drop matches that also contain these terms — useful for jobs, spam, or off-brand noise.">
                          Exclude
                        </FieldLabel>
                      }
                    >
                      <TermChipsField
                        key={open ? 'exclude-open' : 'exclude-closed'}
                        terms={draft.excludeTerms}
                        onChange={(excludeTerms) => setDraft({ ...draft, excludeTerms })}
                        placeholder="jobs, hiring, scam…"
                        accent="exclude"
                        aria-label="Keywords to exclude"
                      />
                    </Field>
                  </>
                ) : (
                  <>
                    <Field
                      label={
                        <FieldLabel
                          required
                          tip="Boolean dialect: AND, OR, NOT, CONTAINS, parentheses, and quoted phrases."
                        >
                          Include query
                        </FieldLabel>
                      }
                    >
                      <AdvancedQueryField
                        id="listening-include"
                        value={draft.queryInclude}
                        onChange={(queryInclude) => {
                          setDraft({ ...draft, queryInclude });
                          setErrors((prev) => clearFieldError(prev, 'queryInclude'));
                        }}
                        error={errors.queryInclude}
                        placeholder='(Fairful OR @fairful) AND NOT (jobs OR hiring)'
                        examples
                      />
                    </Field>

                    <Field
                      label={
                        <FieldLabel tip="Optional. Matches containing these terms are dropped before triage.">
                          Exclude query
                        </FieldLabel>
                      }
                    >
                      <AdvancedQueryField
                        id="listening-exclude"
                        value={draft.queryExclude}
                        onChange={(queryExclude) => {
                          setDraft({ ...draft, queryExclude });
                          setErrors((prev) => clearFieldError(prev, 'queryExclude'));
                        }}
                        error={errors.queryExclude}
                        placeholder='jobs OR careers OR "glassdoor"'
                      />
                    </Field>
                  </>
                )}

                <Field
                  label={
                    <FieldLabel
                      required
                      tip="Where this rule listens — social posts, traditional press, or both."
                    >
                      Channels
                    </FieldLabel>
                  }
                >
                  <ChipMultiSelect
                    options={[...CHANNEL_CHIP_OPTIONS]}
                    value={draft.channels}
                    onChange={(channels) => {
                      setDraft({ ...draft, channels });
                      setErrors((prev) => clearFieldError(prev, 'channels'));
                    }}
                    emptyHint="Select at least one channel"
                    aria-label="Listening channels"
                  />
                  <FieldHint error={errors.channels} />
                </Field>

                <Field label={<FieldLabel>Alert me on</FieldLabel>}>
                  <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
                    {(
                      [
                        ['alert_on_match', 'Any new match', 'Notify when this rule finds coverage'],
                        ['alert_volume_spike', 'Volume spike', 'Unusual jump in match volume'],
                        ['alert_sentiment_drop', 'Sentiment drop', 'Tone turns more negative'],
                      ] as const
                    ).map(([key, title, hint]) => (
                      <label
                        key={key}
                        className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-gray-300"
                          checked={draft[key]}
                          onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-800">{title}</span>
                          <span className="block text-[11px] text-gray-400">{hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </Field>

                {(previewInclude || previewExclude) && draft.editorMode === 'simple' && (
                  <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Query preview
                    </p>
                    <code className="mt-1 block break-all text-xs text-gray-700">
                      {previewInclude || '—'}
                      {previewExclude ? (
                        <span className="text-amber-800"> · exclude {previewExclude}</span>
                      ) : null}
                    </code>
                  </div>
                )}
              </div>

              <DialogFooter className="border-t border-gray-100 bg-gray-50 px-6 py-4 sm:justify-end">
                <Button type="button" variant="outline" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createRule.isPending}>
                  {createRule.isPending ? 'Creating…' : 'Create rule'}
                </Button>
              </DialogFooter>
            </form>
          </TooltipProvider>
        </DialogContent>
      </Dialog>
    </MediaPageShell>
  );
};

export default MediaListeningPage;
